import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MainMenu.css';
import gsap from 'gsap';
import menuBg from '../asserts/ss.mp4';
import Header from '../components/layout/Header';
import Btn2 from '../components/common/Btn2';
import Btn1 from '../components/common/Btn1';
import Preloader from '../components/layout/Preloader';
import SettingsPopup from '../components/layout/SettingsPopup';
import QuestsPopup from '../components/layout/QuestsPopup';
import ProfilePopup from '../components/layout/ProfilePopup';

const TUTORIAL_STEPS = [
  {
    id: 'coins',
    arrow: 'left',
    arrowStyle: { top: '48px', right: '334px' },
    textStyle: { top: '80px', right: '370px' },
    text: 'These are your coins — you have 3 lives. Lose them all and you restart from the beginning!',
  },
  {
    id: 'heart',
    arrow: 'right',
    arrowStyle: { top: '48px', left: '350px' },
    textStyle: { top: '80px', left: '410px' },
    text: 'This is your heart bar — collect heart pieces as you win to fill your progress here.',
  },
];

const Mainmenu = () => {
  const [showPreloader, setShowPreloader] = useState(true);
  const [activePopup, setActivePopup] = useState(null);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showTutorial, setShowTutorial] = useState(() => {
    return sessionStorage.getItem('tutorialDone') !== 'true';
  });
  const [tutorialDone, setTutorialDone] = useState(() => {
    return sessionStorage.getItem('tutorialDone') === 'true';
  });

  const pageRef = useRef();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (showPreloader) return;
    if (tutorialDone) return;

    gsap.set('.main-menu-video', {
      scale: 1.15,
      opacity: 0,
      filter: 'brightness(0.3) blur(12px)',
    });
    gsap.set('.menu-btn', { scale: 0, opacity: 0, y: 40 });
    gsap.set('.play-btn-wrap', { scale: 0, opacity: 0, y: 30 });

  }, [showPreloader]);

  useLayoutEffect(() => {
    if (!tutorialDone) return;
    if (showTutorial) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      tl
        .to('.main-menu-video', {
          scale: 1,
          opacity: 1,
          filter: 'brightness(1) blur(0px)',
          duration: 2,
          ease: 'power3.out',
        })
        .to('.menu-btn', {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 0.9,
          stagger: 0.14,
          ease: 'back.out(2.2)',
        }, '-=0.5')
        .to('.play-btn-wrap', {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'back.out(2.4)',
        }, '-=0.4');

      gsap.to('.play-btn-wrap', {
        y: -6,
        duration: 2.2,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 2.5,
      });
    }, pageRef);

    return () => ctx.revert();
  }, [tutorialDone, showPreloader]);

  const handleTutorialNext = () => {
    if (tutorialStep < TUTORIAL_STEPS.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      setShowTutorial(false);
      setTutorialDone(true);
      sessionStorage.setItem('tutorialDone', 'true');
    }
  };

  if (showPreloader) {
    return <Preloader onComplete={() => setShowPreloader(false)} />;
  }

  const step = TUTORIAL_STEPS[tutorialStep];

  return (
    <div ref={pageRef} className="main-menu">
      <video
        className="main-menu-video"
        src={menuBg}
        autoPlay
        loop
        muted
        playsInline
      />

      <Header />

      <Btn2 btnt="Quests" className="menu-btn quests-btn" onClick={() => setActivePopup('quests')} />
      <Btn2 btnt="Shop" className="menu-btn shop-btn" onClick={() => navigate('/shop')} />
      <Btn2 btnt="Profile" className="menu-btn profile-btn" onClick={() => setActivePopup('profile')} />
      <Btn2 btnt="Settings" className="menu-btn settings-btn" onClick={() => setActivePopup('settings')} />

      <div className="play-btn-wrap" onClick={() => navigate('/levels')}>
        <Btn1 btnt="Play" />
      </div>

      {showTutorial && (
        <div className="tutorial-overlay" onClick={handleTutorialNext}>
          <div className="tutorial-header-spotlight" />

          <div className="tutorial-arrow" style={step.arrowStyle}>
            {step.arrow === 'left' ? (
              <svg width="80" height="40" viewBox="0 0 60 40">
                <path d="M10 20 Q30 5 55 20" stroke="#f5d97a" strokeWidth="3" fill="none" strokeLinecap="round"/>
                <polygon points="55,12 55,28 68,20" fill="#f5d97a"/>
              </svg>
            ) : (
              <svg width="80" height="40" viewBox="0 0 60 40" style={{ transform: 'scaleX(-1)' }}>
                <path d="M10 20 Q30 5 55 20" stroke="#f5d97a" strokeWidth="3" fill="none" strokeLinecap="round"/>
                <polygon points="55,12 55,28 68,20" fill="#f5d97a"/>
              </svg>
            )}
          </div>

          <div className="tutorial-text-box" style={step.textStyle}>
            <p>{step.text}</p>
            <span className="tutorial-tap">Tap to continue</span>
          </div>
        </div>
      )}

      {activePopup === 'settings' && <SettingsPopup onClose={() => setActivePopup(null)} />}
      {activePopup === 'profile' && <ProfilePopup onClose={() => setActivePopup(null)} />}
      {activePopup === 'quests' && <QuestsPopup onClose={() => setActivePopup(null)} />}
    </div>
  );
};

export default Mainmenu;